import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { Modal, Button, Input } from '../custom-components';
import { Device } from '../types';
import { messageI18n } from '../../../../helpers/i18n/toastI18n';

interface CertificateManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  onUpdate: (device: Device, option: 'upload' | 'auto-generate') => void;
}

export const CertificateManagementModal: React.FC<CertificateManagementModalProps> = ({
  isOpen,
  onClose,
  device,
  onUpdate,
}) => {
  const { t } = useTranslation('Organization');
  const [certificateOption, setCertificateOption] = useState<'upload' | 'auto-generate'>('auto-generate');

  const handleSubmit = () => {
    if (device) {
      onUpdate(device, certificateOption);
      if (certificateOption === 'auto-generate') {
        messageI18n.success('devices.certificateAutoGenerateSuccess');
      } else {
        messageI18n.success('devices.certificateUploadSuccess');
      }
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('devices.modals.certificateTitle')}
      description={t('devices.modals.certificateDescription', { name: device?.name })}
    >
      <div className="space-y-4">
        <div className="space-y-3">
          <label className="text-base font-medium text-gray-900 dark:text-white">{t('devices.modals.certificateOption')}</label>
          <div className="grid grid-cols-1 gap-3">
            <div
              className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                certificateOption === 'upload'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setCertificateOption('upload')}
            >
              <input
                type="radio"
                value="upload"
                checked={certificateOption === 'upload'}
                onChange={() => setCertificateOption('upload')}
                className="text-blue-600"
              />
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-blue-600" />
                <span className="text-slate-900 dark:text-white font-medium">
                  {t('devices.modals.uploadCertificates')}
                </span>
              </div>
            </div>
            <div
              className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                certificateOption === 'auto-generate'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setCertificateOption('auto-generate')}
            >
              <input
                type="radio"
                value="auto-generate"
                checked={certificateOption === 'auto-generate'}
                onChange={() => setCertificateOption('auto-generate')}
                className="text-blue-600"
              />
              <div className="flex items-center gap-2">
                <span className="text-slate-900 dark:text-white font-medium">
                  {t('devices.modals.autoGenerateCertificates')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {certificateOption === 'upload' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.certificate.scpCert')}
              </label>
              <Input type="file" accept=".crt,.pem" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.certificate.scpKey')}
              </label>
              <Input type="file" accept=".key,.pem" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.certificate.scuCert')}
              </label>
              <Input type="file" accept=".crt,.pem" />
            </div>
          </div>
        )}

        {certificateOption === 'auto-generate' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
              <span className="text-sm font-medium">
                {t('devices.modals.certificate.autoGenerateTitle')}
              </span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {t('devices.modals.certificate.autoGenerateHint')}
            </p>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>
          {certificateOption === 'auto-generate'
            ? t('devices.common.generateAndUpdate')
            : t('devices.common.uploadAndUpdate')}
        </Button>
      </div>
    </Modal>
  );
}; 